import React, { useContext, useEffect, useState } from 'react';
import { Row, Col, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import 'assets/styles/common.scss';
import { LocaleContext } from 'routes/Router';
import { FormattedMessage, useIntl } from 'react-intl';
import mail from 'assets/images/Dashboard/mail.png';
import call from 'assets/images/Dashboard/call.png';
import trash from 'assets/images/Dashboard/trash.png';
import rightArrow from 'assets/images/Dashboard/arrow-right.png';
import { providerAdminCustomerDetails } from 'routes/AppUrls';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Pagination from '@mui/material/Pagination';
import { getCustomerProviderCallAPI } from 'store/ProviderAdmin/getCustomerList/action';
import { getUserRole } from 'utils/general';
import { deleteCustomerProviderCallAPI } from 'store/ProviderAdmin/deleteCustomer/action';
import ToastNotification from 'components/common/Toast';
import { useConfirm } from 'material-ui-confirm';
import Lottie from 'lottie-react';
import animationData from 'assets/Lottie/no_data.json';
import { ShimmerBox, ShimmerCircle } from 'components/common/Shimmer';
import { getCategoryCallAPI } from 'store/SuperAdmin/getCategory/action';
import { getAppointmentsCallAPI } from 'store/ProviderAdmin/getAppointments/action';
import LabelWithInput from 'components/common/Input/LabelWithInput';
import InputAdornment from '@mui/material/InputAdornment';
import searchIcon from 'assets/images/navbar/search.svg';
import crossIcon from 'assets/images/provider/crossIcon.png';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { setPageTitle } from 'store/pageTitle/action';

const ProviderCustomer = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const [conf, setConf] = ToastNotification();
  const userRole = getUserRole();
  const intl = useIntl();
  const { locale } = useContext(LocaleContext);

  const [page, setPage] = useState(1); // Current page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Items per page

  const [searchValue, setSearchValue] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const customerListData = useSelector(
    (state) => state?.getCustomerProviderReducer?.data
  );
  const customerLoading = useSelector(
    (state) => state?.getCustomerProviderReducer?.loading
  );
  const services = useSelector((state) => state?.getCategoryReducer?.data);
  const appointmentDays = useSelector(
    (state) => state?.getAppointmentsReducer?.data
  );
  const servicesLoading = useSelector(
    (state) => state?.getCategoryReducer?.loading
  );
  const appointmentDaysLoading = useSelector(
    (state) => state?.getAppointmentsReducer?.loading
  );
  const [totalPages, setTotalPages] = useState(
    Math.ceil((customerListData?.count || 0) / rowsPerPage)
  );
  const [customerList, setCustomerList] = useState(
    customerListData ? customerListData : []
  );

  useEffect(() => {
    getCustomerListApi(rowsPerPage, page);
    dispatch(setPageTitle('Customers'));
    dispatch(getCategoryCallAPI({}));
    dispatch(getAppointmentsCallAPI({}));
  }, []);

  const hasServices = services?.data?.length > 0;
  const hasSchedule = appointmentDays?.data?.length > 0;

  const isSetupIncomplete = !hasServices || !hasSchedule;
  const isSetupLoading = servicesLoading || appointmentDaysLoading;

  const getCustomerListApi = (pageSize, page) => {
    dispatch(
      getCustomerProviderCallAPI(
        { pageSize, page },
        {
          run: true,
          handleFunction: (res) => handleSuccess(res),
          navigate: navigate,
        }
      )
    );
  };

  const handleSuccess = (res) => {
    setCustomerList(res?.data);
    setFilteredCustomers(res?.data?.results || []);
    setTotalPages(Math.ceil((res?.data?.count || 0) / rowsPerPage));
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    getCustomerListApi(rowsPerPage, value);
  };

  const handleDeleteCustomer = (key) => {
    confirm({
      title: 'Confirm Delete',
      description: 'Are you sure you want to delete Customer ?',
      confirmationText: 'Yes',
      cancellationText: 'No',
      dialogProps: { maxWidth: 'xs' },
      confirmationButtonProps: {
        sx: {
          bgcolor: '#292D32',
          color: '#FFFFFF',
          borderRadius: '0px',
          border: '1px solid #292D32',
          '&:hover': { bgcolor: '#292D32' },
        },
      },
      cancellationButtonProps: {
        sx: {
          bgcolor: 'transparent',
          color: '#292D32',
          borderRadius: '0px',
          border: '1px solid #292D32',
        },
      },
    })
      .then(() => handleConfirmDeleteCustomer(key))
      .catch(() => console.log('Deletion cancelled.'));
  };

  const handleConfirmDeleteCustomer = (key) => {
    dispatch(
      deleteCustomerProviderCallAPI(
        {},
        {
          id: key?.id,
          run: true,
          handleFunction: (res) => handleDeleteSuccess(res),
          navigate: navigate,
        }
      )
    );
  };

  const handleDeleteSuccess = (res) => {
    setConf({
      msg: res?.data?.message,
      variant: 'success',
    });
    getCustomerListApi(rowsPerPage, page);
  };

  const handleSearch = (value) => {
    setSearchValue(value);

    if (!value) {
      setFilteredCustomers(customerList?.results || []);
      return;
    }

    const filtered = customerList?.results?.filter((item) => {
      const search = value.toLowerCase().trim();

      return (
        (item?.name?.name_en || '').toLowerCase().includes(search) ||
        (item?.email || '').toLowerCase().includes(search) ||
        (item?.contact_number || '')
          .replace(/\s+/g, '')
          .includes(search.replace(/\s+/g, ''))
      );
    });
    setFilteredCustomers(filtered);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    setFilteredCustomers(customerList?.results || []);
  };

  const handleSort = (f) => {
    const isAsc = sortField === f && sortOrder === 'asc';
    const order = isAsc ? 'desc' : 'asc';
    const dir = order === 'asc' ? 1 : -1;

    const sorted = [...filteredCustomers].sort((a, b) => {
      const getVal = (i) =>
        ({
          name: (i?.name?.name_en || '').toLowerCase(),
          last_appointment: new Date(i?.last_appointment || 0).getTime(),
        })[f] ?? (i[f] || '').toString().toLowerCase();

      return getVal(a) > getVal(b) ? dir : -dir;
    });

    setSortField(f);
    setSortOrder(order);
    setFilteredCustomers(sorted);
  };

  const renderSortArrow = (field) => {
    if (sortField !== field) return null;
    const Icon = sortOrder === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
    return <Icon sx={{ fontSize: '16px', ml: 0.5, verticalAlign: 'middle' }} />;
  };

  return (
    <Row className="bc-F2F3F6 m-0 p-0">
      <Col
        className={
          locale === 'en'
            ? 'commonEnContainer lg:ml-left'
            : 'commonArContainer lg:mr-right'
        }
      >
        {/* <h2 className="fc-292D32 fs-32 fw-400 ff-Akkurat_Bold m-0">
          <FormattedMessage id="Customers" />
        </h2> */}

        <Row className="p-0 mt-3">
          <Col md={12} lg={4} xl={4} xxl={3}>
            <LabelWithInput
              placeholder={intl.formatMessage({ id: 'Search' })}
              label={intl.formatMessage({ id: 'Search' })}
              name="search"
              type="text"
              marginLabelTop="0"
              height="48"
              bgColor="#FFFFFF"
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <img
                      src={searchIcon}
                      alt="search"
                      style={{
                        cursor: 'pointer',
                        height: '20px',
                        width: '20px',
                      }}
                    />
                    {searchValue && (
                      <img
                        src={crossIcon}
                        alt="clear"
                        width={18}
                        height={18}
                        style={{ cursor: 'pointer', marginLeft: '8px' }}
                        onClick={handleSearchClear}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Col>
        </Row>

        <Row className="mt-3 listCustomHeader m-0 p-0 flex items-center">
          <Col lg={2} md={6} sm={12}>
            <p
              className="p-0 m-0 fs-14 fw-400 fc-292D32 ff-Akkurat_Bold d-flex cursor-pointer hover:text-gray-700"
              onClick={() => handleSort('name')}
            >
              <FormattedMessage id="Customer & ID" />
              {renderSortArrow('name')}
            </p>
          </Col>
          <Col lg={3} md={6} sm={12}>
            <p className="p-0 m-0 fs-14 fw-400 fc-292D32 ff-Akkurat_Bold">
              <FormattedMessage id="Location" />
            </p>
          </Col>
          <Col lg={3} md={6} sm={12}>
            <p
              className="p-0 m-0 fs-14 fw-400 fc-292D32 ff-Akkurat_Bold d-flex cursor-pointer hover:text-gray-700"
              onClick={() => handleSort('email')}
            >
              <FormattedMessage id="Contact Info" />
              {renderSortArrow('email')}
            </p>
          </Col>
          <Col lg={2} md={6} sm={12}>
            <p
              className="p-0 m-0 fs-14 fw-400 fc-292D32 ff-Akkurat_Bold d-flex cursor-pointer hover:text-gray-700"
              onClick={() => handleSort('last_Appointment')}
            >
              <FormattedMessage id="Last Appointment" />
              {renderSortArrow('last_Appointment')}
            </p>
          </Col>
          <Col lg={2} md={6} sm={12}>
            <p className="p-0 m-0 fs-14 fw-400 fc-292D32 ff-Akkurat_Bold">
              <FormattedMessage id="Actions" />
            </p>
          </Col>
        </Row>

        <Row className="bc-FFFFFF m-0 p-0">
          {customerLoading ? (
            <>
              {[1, 2, 3, 4, 5].map((item) => (
                <Row
                  key={item}
                  className="m-0 p-0 flex items-center listAppointment"
                >
                  <Col lg={2} md={6} sm={12}>
                    <ShimmerBox width="150px" height="14px" />
                    <ShimmerBox width="100px" height="12px" className="mt-2" />
                  </Col>
                  <Col lg={3} md={6} sm={12}>
                    <ShimmerBox width="200px" height="12px" />
                  </Col>
                  <Col lg={3} md={6} sm={12}>
                    <ShimmerBox width="120px" height="12px" />
                    <ShimmerBox width="100px" height="12px" className="mt-2" />
                  </Col>
                  <Col lg={2} md={6} sm={12}>
                    <ShimmerBox width="100px" height="14px" />
                  </Col>
                  <Col lg={2} md={6} sm={12}>
                    <Stack
                      direction="horizontal"
                      gap={3}
                      className="items-center"
                    >
                      <ShimmerBox width="24px" height="24px" />
                      <ShimmerBox width="100px" height="14px" />
                    </Stack>
                  </Col>
                </Row>
              ))}
            </>
          ) : filteredCustomers?.length > 0 ? (
            filteredCustomers.map((key, index) => (
              <Row className="m-0 p-0 flex items-center listAppointment">
                <Col lg={2} md={6} sm={12}>
                  <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold">
                    {key?.name?.name_en}
                  </p>
                  <p className="p-0 m-0 fs-12 fw-400 fc-292D32 ff-Akkurat">
                    {key?.id}
                  </p>
                </Col>

                <Col lg={3} md={6} sm={12}>
                  <p className="p-0 m-0 fs-12 fc-292D32 fw-400 ff-Akkurat mt-1 line-clamp-2 text-ellipsis overflow-hidden">
                    {key?.address}
                  </p>
                </Col>
                <Col lg={3} md={6} sm={12}>
                  <p className="p-0 m-0 fs-12 fw-400 fc-292D32 ff-Akkurat_Bold">
                    <Stack
                      direction="horizontal"
                      gap={2}
                      className="items-center "
                    >
                      <img
                        src={mail}
                        alt="icon"
                        style={{ height: '20px', width: '20px' }}
                      />
                      <p className="p-0 m-0 fs-12 fc-292D32 fw-400 ff-Akkurat">
                        {key?.email}
                      </p>
                    </Stack>
                    <Stack
                      direction="horizontal"
                      gap={2}
                      className="items-center mt-2"
                    >
                      <img
                        src={call}
                        alt="icon"
                        style={{ height: '20px', width: '20px' }}
                      />
                      <p className="p-0 m-0 fs-12 fc-292D32 fw-400 ff-Akkurat">
                        {key?.contact_number}
                      </p>
                    </Stack>
                  </p>
                </Col>
                <Col lg={2} md={6} sm={12}>
                  <p className="p-0 m-0 fs-14 fw-400 fc-292D32 ff-Akkurat">
                    {key?.last_appointment !== null &&
                    key?.last_appointment !== undefined
                      ? moment(key?.last_appointment).format('DD MMM YYYY')
                      : ''}
                  </p>
                </Col>
                <Col lg={2} md={6} sm={12}>
                  <Stack
                    direction="horizontal"
                    gap={3}
                    className="items-center"
                  >
                    <img
                      className="cursor-pointer"
                      src={trash}
                      alt="icon"
                      style={{ height: '24px', width: '24px' }}
                      onClick={() => handleDeleteCustomer(key)}
                    />
                    <Stack
                      direction="horizontal"
                      gap={2}
                      className="items-center cursor-pointer"
                      onClick={() =>
                        navigate(providerAdminCustomerDetails(locale), {
                          state: { id: key?.id },
                        })
                      }
                    >
                      <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold underline underline-offset-4">
                        <FormattedMessage id="View Details" />
                      </p>
                      <img
                        src={rightArrow}
                        alt="icon"
                        style={{ height: '20px', width: '20px' }}
                      />
                    </Stack>
                  </Stack>
                </Col>
              </Row>
            ))
          ) : isSetupLoading ? null : (
            <Row className="bc-FFFFFF m-0 p-50 flex justify-center items-center">
              <Lottie
                animationData={animationData}
                loop={true}
                style={{ height: '200px', width: '200px' }}
              />
              <p className="mt-3 text-center fs-16 fw-400 fc-9A9A9A ff-Akkurat">
                {isSetupIncomplete ? (
                  <FormattedMessage id="Add your services and set a schedule so customers can book and find you" />
                ) : (
                  <FormattedMessage id="Your customer list is empty" />
                )}
              </p>
            </Row>
          )}
        </Row>

        {customerList?.count > 10 && (
          <Row className="mt-3 m-0 p-0">
            <Stack spacing={2} className="p-0">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
                className="d-flex justify-end"
              />
            </Stack>
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default ProviderCustomer;
